import { createRouter, createWebHistory } from "vue-router";
import { setTitlePage } from "./guards";
import routes from "./routes";
import { capturePageview, setSurveyContext } from "@/lib/analytics";
import {
  handleRoutingError,
  handleUser,
  checkUserAccess,
  rememberRoutingTarget,
  fetchSurveyResources,
  handleMCampaign,
  handlePricerProject,
  handleTemplate,
  handleJob,
  handleEmailCampaign,
  handleMailingList,
  handleSurveyExport,
} from "./guards";

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  rememberRoutingTarget(to);
  try {
    await handleUser(to);
    await checkUserAccess(to);
  } catch (error) {
    return handleRoutingError(error, to);
  }
  return true;
});

// vue-router skips a record's beforeEnter when only the params change (entity-to-entity
// jump on the same page), so re-run the record's data loader here (meta.paramLoader).
const PARAM_LOADERS = {
  survey: fetchSurveyResources,
  mCampaign: handleMCampaign,
  pricerProject: handlePricerProject,
  mailingTemplate: handleTemplate,
  job: handleJob,
  emailCampaign: handleEmailCampaign,
  mailingList: handleMailingList,
  surveyExport: handleSurveyExport,
};

router.beforeEach(async (to, from) => {
  const records = to.matched.filter((r) => r.meta.paramLoader && from.matched.includes(r));

  for (const record of records) {
    const key = record.meta.paramKey ?? "token";
    if (to.params[key] === from.params[key]) continue;

    try {
      const result = await PARAM_LOADERS[record.meta.paramLoader](to);
      if (result instanceof Error) throw result;
    } catch (error) {
      return handleRoutingError(error, to);
    }
  }
  return true;
});
// custom meta tags method to inject metaTags attributes from routes.js to HMTL of the page
router.beforeResolve((to) => {
  document.title = to.meta.title || "my default title";
  if ("metaTags" in to.meta) {
    for (let x = 0; x < to.meta.metaTags.length; x++) {
      let oldMeta = document.getElementsByTagName("meta");
      for (let m = 0; m < oldMeta.length; m++) {
        if (oldMeta[m].name == to.meta.metaTags[x].name) {
          oldMeta[m].remove();
        }
      }
      let meta = document.createElement("meta");
      if ("name" in to.meta.metaTags[x]) {
        meta.name = to.meta.metaTags[x].name;
        meta.content = to.meta.metaTags[x].content;
      }
      document.head.appendChild(meta);
    }
  }
});
const surveyTokenOf = (to) =>
  to.matched.some((record) => record.meta.paramLoader === "survey") ? to.params.token : undefined;

router.afterEach((to, from, failure) => {
  setTitlePage(to);
  document.title = `${to.meta.title}`;
  if (failure) return true;

  setSurveyContext(surveyTokenOf(to));
  capturePageview(to.name);
  return true;
});

export default router;
